import React, { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Layout Components
import { DashboardLayout } from '../../components/layout';

// Feature Components
// GeofenceBreachAlert is deliberately NOT imported — see the alerts block below.
import { EditSeniorModal, DeleteSeniorModal, ActivationCodeModal, EscalationAlert, TrialIncompleteBanner, ScamBlockingInactiveBanner, CaregiverSmsBanners, ManagedModePinBanner, CallActivityPanel, VoicemailIndicator, LocationPanel, BatteryAlertsDrawer, VoicemailDrawer, MedicationRemindersDrawer, ActionTile } from '../../components/features';
import FallDetectionAlert from '../../components/features/FallDetectionAlert';

// Panel Components (right-side drawers)
import { SettingsDrawer } from '../../components/panels/SettingsDrawer';
import { ContactsDrawer } from '../../components/panels/ContactsDrawer';
import { RemoteLockDrawer } from '../../components/panels/RemoteLockDrawer';
import { GPSZoneDrawer } from '../../components/panels/GPSZoneDrawer';
import { ManagedModeDrawer } from '../../components/panels/ManagedModeDrawer';
import { ScamBlockingDrawer } from '../../components/panels/ScamBlockingDrawer';
import { CurrentModelDrawer } from '../../components/panels/CurrentModelDrawer';
import { BillingDrawer } from '../../components/panels/BillingDrawer';
import { AccountMenu } from '../../components/layout/AccountMenu';

// Hooks
import { useSeniors, useGreeting, Senior, useCaregiver } from '../../hooks';
import { useVoicemailFlag } from '../../hooks/useVoicemailFlag';
import { useAuth } from '../../context/AuthContext';

// Layout name lookup
import { getLayoutById, getLayoutImage } from '../../constants/layouts';
import { FEATURES } from '../../constants/featureFlags';
import { formatBatteryAlertState } from '../../constants/batteryAlerts';

const Dashboard: FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { seniors, loading, refetch } = useSeniors();
  const { greeting, formattedDate } = useGreeting();
  const { user, signOut } = useAuth();
  const { caregiver } = useCaregiver();

  // The tab title used to stay on whatever the previous page set (usually
  // "Connexion — AidFone"), so the dashboard was unfindable among open tabs.
  useEffect(() => {
    document.title = i18n.language.startsWith('fr')
      ? 'Tableau de bord — AidFone'
      : 'Dashboard — AidFone';
  }, [i18n.language]);

  const currentUser = {
    name: caregiver?.full_name || user?.email?.split('@')[0] || t('dashboard.caregiverFallback'),
  };
  const senior = seniors[0] || null; // B2C: single senior

  // Modal / drawer states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [contactsModalOpen, setContactsModalOpen] = useState(false);
  const [seniorModalOpen, setSeniorModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [geofenceModalOpen, setGeofenceModalOpen] = useState(false);
  const [remoteLockDrawerOpen, setRemoteLockDrawerOpen] = useState(false);
  const [batteryAlertsOpen, setBatteryAlertsOpen] = useState(false);
  const [voicemailDrawerOpen, setVoicemailDrawerOpen] = useState(false);
  const [medicationDrawerOpen, setMedicationDrawerOpen] = useState(false);
  const [managedModeDrawerOpen, setManagedModeDrawerOpen] = useState(false);
  const [scamBlockingOpen, setScamBlockingOpen] = useState(false);
  const [currentModelOpen, setCurrentModelOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  // Bumped when the Managed Mode drawer closes so the PIN banner re-checks.
  const [pinBannerKey, setPinBannerKey] = useState(0);

  const [caregiverId] = useState('default-caregiver-id');

  const layoutConfig = senior?.layout_id ? getLayoutById(senior.layout_id) : null;

  // D4 — voicemail state for the tile. Same hook the pill used, so the tile
  // flips in realtime when the phone clears or receives an MWI.
  // NOTE: voicemail_flag stores a BOOLEAN `pending`, not a count — Android's
  // MWI is itself a flag. So the tile can say "1 waiting" but never "3
  // waiting"; the count is pinned to 1 rather than invented.
  const { pending: voicemailPending } = useVoicemailFlag(senior?.id);

  // F05 / D13 — the TILE carries only ON/OFF. The composed detail
  // ("voice 40% + SMS 25%") moved inside BatteryAlertsDrawer: on a tile,
  // "SMS 25%" reads like a battery level rather than an alert threshold.
  // Undefined until the configuration row loads — no state, no claim.
  const batteryKnown =
    typeof senior?.voice_nag_enabled === 'boolean' &&
    typeof senior?.lowbat_sms_enabled === 'boolean';
  const batteryAnyOn =
    senior?.voice_nag_enabled === true || senior?.lowbat_sms_enabled === true;
  const batteryTileState = !batteryKnown
    ? undefined
    : batteryAnyOn
    ? t('dashboard.actions.battery.tileOn')
    : t('dashboard.actions.battery.tileOff');

  // Build the recent activity timeline entries from the senior's current state.
  // TODO Phase 8: replace with real activity_events table data.
  type ActivityEntry = { icon: string; bg: string; fg: string; label: string; time?: string };
  const activityEntries: ActivityEntry[] = [];
  if (senior?.status === 'online') {
    activityEntries.push({
      icon: 'wifi',
      bg: 'bg-emerald-50',
      fg: 'text-emerald-600',
      label: t('dashboard.activity.phoneOnline'),
      time: t('dashboard.now'),
    });
  }
  if (senior?.activated) {
    activityEntries.push({
      icon: 'check_circle',
      bg: 'bg-brand-primary/10',
      fg: 'text-brand-primary',
      label: t('dashboard.activity.deviceActivated'),
    });
  }
  if (senior?.has_payment) {
    activityEntries.push({
      icon: 'credit_card',
      bg: 'bg-emerald-50',
      fg: 'text-emerald-600',
      label: t('dashboard.activity.subscriptionStarted'),
    });
  }
  if (senior) {
    activityEntries.push({
      icon: 'phone_android',
      bg: 'bg-amber-50',
      fg: 'text-amber-600',
      label: t('dashboard.activity.configCreated', { name: senior.name }),
    });
  }

  // Profile display
  const getProfileDisplayName = (type?: string) => {
    switch (type) {
      case 'cognitive': return t('dashboard.profile.cognitive');
      case 'physical': return t('dashboard.profile.physical');
      case 'visual': return t('dashboard.profile.visual');
      default: return t('dashboard.profile.notSet');
    }
  };

  // Battery helpers
  const getBatteryIcon = (level?: number) => {
    if (level === undefined) return 'battery_unknown';
    if (level >= 80) return 'battery_full';
    if (level >= 50) return 'battery_5_bar';
    if (level >= 20) return 'battery_3_bar';
    return 'battery_1_bar';
  };

  const getBatteryColor = (level?: number) => {
    if (level === undefined) return 'text-gray-400';
    if (level >= 50) return 'text-emerald-500';
    if (level >= 20) return 'text-amber-500';
    return 'text-red-500';
  };

  return (
    <DashboardLayout
      currentUser={currentUser}
      onLogout={signOut}
      seniorName={senior?.name}
      onSeniorNavClick={() => setEditModalOpen(true)}
    >
      {/* Alerts */}
      {/* Geofencing is CUT from launch scope, so <GeofenceBreachAlert> is not
          rendered AND not imported. The Android release ships
          GEOFENCING_ENABLED=false so it could never fire anyway, but it was
          mounted unconditionally and — this is the part a feature flag does not
          fix — its "has left the safe zone" copy still shipped inside the
          production JS bundle. Gating alone does not tree-shake a component that
          is still imported; dropping the import does. Same lesson as
          src/i18n/parked/remoteLock.wave1-5.json.

          To restore: re-add it to the import above and render it behind
          FEATURES.GEOFENCING_ENABLED, in lock-step with the Android flag. */}
      {/* Wave-2, OFF at launch. The server does not register the
          fall-detection routes (FALL_DETECTION_ENABLED), so this component's
          acknowledge / false-alarm calls would 404 — a dismiss button that
          looks like it worked is worse than no alert at all. */}
      {FEATURES.FALL_DETECTION_ENABLED && <FallDetectionAlert caregiverId={caregiverId} />}
      <EscalationAlert seniorIds={seniors.map(s => s.id)} />

      {/* Zone 1 — D19 (2026-08-10): slim top bar.
          Was a 42px greeting headline over a second line, eating the top of
          every load before anything actionable. Same warmth, one small line,
          with the account controls where people look for them — top right. */}
      <header className="relative flex items-center justify-between gap-3 py-1">
        <p className="text-sm text-brand-text-muted font-medium truncate min-w-0">
          {senior ? (
            <>
              {greeting}, <span className="text-brand-text font-semibold">{currentUser.name.split(' ')[0]}</span>
              <span className="mx-1.5 text-brand-text-muted/50">—</span>
              {t('dashboard.watchingOver')}{' '}
              <span className="text-brand-text font-semibold">{senior.name}</span>
            </>
          ) : (
            <>
              {greeting}, <span className="text-brand-text font-semibold">{currentUser.name.split(' ')[0]}</span>
            </>
          )}
          {/* Date kept, but subordinate — hidden on phones where the line is tightest. */}
          <span className="hidden md:inline">
            <span className="mx-1.5 text-brand-text-muted/50">·</span>
            {formattedDate}
          </span>
        </p>

        <AccountMenu
          name={currentUser.name}
          email={user?.email}
          onProfile={() => navigate('/account')}
          onChangePassword={() => navigate('/forgot-password')}
          onBilling={() => setBillingOpen(true)}
          onSignOut={signOut}
        />
      </header>
      <div className="h-px bg-gradient-to-r from-brand-border via-brand-border/40 to-transparent mb-1" />

      {/* Per-caregiver SMS status banners — opt-out (replied STOP) and
          missing/invalid phone. Renders once above the senior cards because
          both flags live on the caregivers row, not per-senior. */}
      <CaregiverSmsBanners />

      {loading ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 animate-pulse">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-gray-100" />
            <div className="flex-1">
              <div className="h-6 bg-gray-100 rounded w-40 mb-2" />
              <div className="h-4 bg-gray-100 rounded w-56" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="h-20 bg-gray-100 rounded-xl" />
            <div className="h-20 bg-gray-100 rounded-xl" />
            <div className="h-20 bg-gray-100 rounded-xl" />
          </div>
        </div>
      ) : !senior ? (
        /* No senior configured yet */
        <div className="bg-white rounded-2xl border border-gray-200 p-8 sm:p-12 text-center">
          <div className="w-20 h-20 rounded-full bg-brand-primary/5 flex items-center justify-center mx-auto mb-4">
            <span className="material-symbols-outlined text-brand-primary text-4xl">phone_android</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('dashboard.emptyState.title')}</h2>
          <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
            {t('dashboard.emptyState.description')}
          </p>
          <button
            onClick={() => navigate('/configure')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-brand-primary text-white font-semibold rounded-xl hover:bg-brand-primary/90 transition-colors"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>add_circle</span>
            {t('dashboard.emptyState.button')}
          </button>
        </div>
      ) : (
        <>
          {/* Managed Mode ships ON but the wizard never asks for a PIN, and the
              server refuses activation until one exists. Surface that here so the
              caregiver is not sent to a dead end at activation time. */}
          <ManagedModePinBanner
            seniorId={senior.id}
            seniorName={senior.name}
            refreshKey={pinBannerKey}
            onSetPin={() => setManagedModeDrawerOpen(true)}
          />

          {/* Trial incomplete banner — shown when signup saved but Stripe checkout was abandoned */}
          {!senior.stripe_customer_id && (
            <TrialIncompleteBanner seniorId={senior.id} seniorName={senior.name} />
          )}

          {/* Scam-blocking inactive banner — Android reports false when the Call Screening role isn't held.
              undefined (device hasn't reported yet) is intentionally not shown — keeps it quiet pre-rollout. */}
          {senior.scamBlockingActive === false && (
            <ScamBlockingInactiveBanner seniorName={senior.name} />
          )}

          {/* Zone 3 — Senior Hero Card */}
          <section
            className="relative rounded-[24px] overflow-hidden border border-brand-border/70"
            style={{
              background:
                'linear-gradient(135deg, #ffffff 0%, #fdfcfb 40%, #faf8f5 100%)',
              boxShadow:
                '0 1px 2px rgba(44, 62, 74, 0.04), 0 12px 40px -18px rgba(44, 62, 74, 0.12)',
            }}
          >
            {/* Decorative ambient blob */}
            <div
              aria-hidden
              className="absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-[0.06] blur-3xl pointer-events-none"
              style={{
                background:
                  'radial-gradient(circle, #1e5f8a 0%, #e07b4c 100%)',
              }}
            />

            {/* Senior identity + edit button */}
            <div className="relative p-6 sm:p-7 flex items-start gap-5">
              {/* Avatar with status ring */}
              <div className="relative flex-shrink-0">
                <div
                  className={`absolute inset-0 rounded-full ${
                    senior.status === 'online' ? 'animate-[pulse_3s_ease-in-out_infinite]' : ''
                  }`}
                  style={{
                    background:
                      senior.status === 'online'
                        ? 'conic-gradient(from 0deg, #2d8a5f, #1e5f8a, #2d8a5f)'
                        : 'conic-gradient(from 0deg, #d4cfc8, #e5e1dc, #d4cfc8)',
                    padding: '2.5px',
                  }}
                >
                  <div
                    className="w-full h-full rounded-full font-display font-semibold text-white flex items-center justify-center text-2xl sm:text-[28px]"
                    style={{
                      background:
                        senior.status === 'online'
                          ? 'linear-gradient(135deg, #1e5f8a 0%, #2d8a5f 100%)'
                          // Light stop raised from #8a9aa6: the white initial
                          // sat at 2.9:1 on it, under the 3:1 large-text floor.
                          : 'linear-gradient(135deg, #646f78 0%, #5a6d7a 100%)',
                      width: 'calc(100% - 0px)',
                      height: 'calc(100% - 0px)',
                    }}
                  >
                    {senior.name.charAt(0).toUpperCase()}
                  </div>
                </div>
                <div className="w-[72px] h-[72px] sm:w-20 sm:h-20 invisible" />
                {/* Status dot */}
                <span
                  className={`absolute bottom-0.5 right-0.5 w-4 h-4 rounded-full border-[3px] border-white ${
                    senior.status === 'online' ? 'bg-brand-success' : 'bg-brand-text-muted/40'
                  }`}
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-display text-2xl sm:text-[28px] font-semibold text-brand-text tracking-[-0.01em] leading-tight truncate">
                    {senior.name}
                  </h2>
                  {/* D4 (2026-08-10) — the voicemail pill moved out of the name row
                      and became a full "Voicemail" tile carrying the same live
                      state. VoicemailIndicator itself is kept: it is still the
                      read-only pill used elsewhere. */}
                </div>
                <div className="flex items-center gap-1.5 mt-1 text-sm text-brand-text-muted">
                  <span
                    className={`inline-block w-1.5 h-1.5 rounded-full ${
                      senior.status === 'online' ? 'bg-brand-success' : 'bg-brand-text-muted/60'
                    }`}
                  />
                  {/* D14 (2026-08-10) — the status TEXT that used to sit here
                      ("Online · Connected") is gone. It duplicated the stats-row
                      status block below, which now states connection state once.
                      The coloured dot stays: it is a glanceable cue next to the
                      name, not a third restatement of the words. */}
                </div>
              </div>

              {/* Edit button */}
              <button
                onClick={() => setSeniorModalOpen(true)}
                className="p-2 rounded-xl text-brand-text-muted hover:bg-white hover:text-brand-primary hover:shadow-sm transition-all flex-shrink-0"
                title={t('dashboard.editSeniorAria')}
                aria-label={t('dashboard.editSeniorAria')}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                  edit
                </span>
              </button>
            </div>

            {/* D21 (2026-08-10) — the three stats are now ONE tight inline
                strip in the same band as the name, not three tall cards in a
                section of their own. That reclaimed roughly a third of the hero's
                height. Per D15 they wrap rather than squeeze: at 360px each stat
                drops to its own line at full size instead of being truncated. */}
            <div className="relative px-6 sm:px-7 pb-4 -mt-1">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2.5">
                {/* Battery */}
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`material-symbols-outlined ${getBatteryColor(senior.battery)}`}
                    style={{ fontSize: '20px' }}
                  >
                    {getBatteryIcon(senior.battery)}
                  </span>
                  <span
                    className={`font-display text-lg font-semibold ${
                      senior.battery !== undefined ? getBatteryColor(senior.battery) : 'text-brand-text-muted/60'
                    }`}
                  >
                    {senior.battery !== undefined ? `${senior.battery}%` : '—'}
                  </span>
                </div>

                <span className="hidden sm:inline text-brand-border" aria-hidden="true">|</span>

                {/* Status — D14's single status, unchanged in meaning */}
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`material-symbols-outlined ${
                      senior.status === 'online' ? 'text-brand-success' : 'text-brand-text-muted'
                    }`}
                    style={{ fontSize: '20px' }}
                  >
                    {senior.status === 'online' ? 'wifi' : 'schedule'}
                  </span>
                  <span
                    className={`font-display text-lg font-semibold leading-snug truncate ${
                      senior.status === 'online' ? 'text-brand-success' : 'text-brand-text'
                    }`}
                  >
                    {senior.status === 'online'
                      ? t('dashboard.heroCard.online')
                      : senior.last_seen
                      ? t('dashboard.heroCard.offlineSince', { time: senior.last_seen })
                      : t('dashboard.heroCard.offline')}
                  </span>
                </div>

                <span className="hidden sm:inline text-brand-border" aria-hidden="true">|</span>

                {/* Current AidFone model — still opens the compact D10 panel */}
                <button
                  type="button"
                  onClick={() => setCurrentModelOpen(true)}
                  className="flex items-center gap-2 min-w-0 group rounded-lg -mx-1 px-1 hover:bg-white/60 transition-colors"
                  title={t('dashboard.heroCard.currentModel')}
                >
                  <span className="material-symbols-outlined text-brand-primary" style={{ fontSize: '20px' }}>
                    phone_android
                  </span>
                  <span className="font-display text-lg font-semibold text-brand-text leading-snug truncate group-hover:text-brand-primary transition-colors">
                    {layoutConfig?.name || senior.layout_id || '—'}
                  </span>
                </button>
              </div>
            </div>

            {/* D11 (2026-08-10) — the chip row is gone entirely. Every chip it
                held now lives on a tile that says the same thing with more
                room: the activation code and activation STATE on the
                "Activation" tile, the contact count in "Manage contacts", GPS
                on its own tile. A row of pills restating tile content was pure
                duplication and it pushed the tiles below the fold. */}
          </section>

          {/* Zone 4 — Quick Actions. Promoted above the data panels so the
              caregiver lands on the primary interactions (settings, contacts,
              remote lock, battery alerts) without scrolling past the long
              activity grid + map. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-1">
            {/* D3 — activation code. First in the grid: for a caregiver whose
                phone is not paired yet, this is the only tile that matters. */}
            <ActionTile
              icon="qr_code_2"
              tone="primary"
              title={t('dashboard.actions.activationCode.title')}
              subtitle={
                senior?.activated === undefined
                  ? t('dashboard.actions.activationCode.sub')
                  : senior.activated
                  ? t('dashboard.actions.activationCode.activated')
                  : t('dashboard.actions.activationCode.notActivated')
              }
              state={senior?.activated}
              onClick={() => setCodeModalOpen(true)}
            />

            {/* D4 — voicemail, with live state. Always rendered: a tile that
                appears and disappears makes the whole grid reflow, so `pending
                === null` (no voicemail_flag row — sharing off, or the phone has
                never reported) gets its own honest third state rather than
                being hidden or mislabelled "none". CEO ruling 2026-08-10.
                No state dot in that case — unknown is not "off". */}
            {/* D12 — state sits beside the title at the same size, not in the
                small subtitle line. */}
            <ActionTile
              icon="voicemail"
              tone="amber"
              title={t('dashboard.actions.voicemail.title')}
              titleState={
                voicemailPending === null
                  ? t('dashboard.actions.voicemail.notShared')
                  : voicemailPending
                  ? t('dashboard.actions.voicemail.waiting', { count: 1 })
                  : t('dashboard.actions.voicemail.none')
              }
              state={voicemailPending ?? undefined}
              onClick={() => setVoicemailDrawerOpen(true)}
            />

            {/* D7 — GPS location, promoted from a chip.
                Back to title-only (2026-08-16). The CEO subtitle supplied
                2026-08-10 read "Location & safe zone" / "Position et zone de
                sécurité", and the safe zone is CUT from v1 — the tile would
                have gone on advertising the feature this PR removes. Rather
                than author replacement copy, the key is dropped and the tile
                returns to the title-only state it shipped in before 2026-08-10.
                ActionTile documents title-only as the supported shape for a
                tile with no approved subtitle string. */}
            <ActionTile
              icon="location_on"
              tone="blue"
              title={t('dashboard.chips.gpsSafeZone')}
              onClick={() => setLocationModalOpen(true)}
            />

            {/* D8 — scam-call blocking, lifted out of "Update phone settings".
                No state dot until the configuration row is read: this is a
                safety switch, and rendering "Off" about an unread value would
                be a false alarm. */}
            <ActionTile
              icon="verified_user"
              tone="red"
              title={t('dashboard.actions.scamBlocking.title')}
              subtitle={
                senior?.call_screening_enabled === undefined
                  ? undefined
                  : senior.call_screening_enabled
                  ? t('dashboard.actions.scamBlocking.on')
                  : t('dashboard.actions.scamBlocking.off')
              }
              state={senior?.call_screening_enabled}
              onClick={() => setScamBlockingOpen(true)}
            />

            <button
              onClick={() => setEditModalOpen(true)}
              className="group relative flex items-center gap-4 p-5 bg-white border border-brand-border/70 rounded-2xl text-left overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:border-brand-primary/30 hover:shadow-[0_8px_30px_-12px_rgba(30,95,138,0.25)]"
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(30, 95, 138, 0.12) 0%, rgba(30, 95, 138, 0.06) 100%)',
                }}
              >
                <span
                  className="material-symbols-outlined text-brand-primary"
                  style={{ fontSize: '22px', fontVariationSettings: "'FILL' 1" }}
                >
                  tune
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-[15px] font-semibold text-brand-text leading-tight">
                  {t('dashboard.actions.settings.title')}
                </div>
                <div className="text-xs text-brand-text-muted mt-0.5">{t('dashboard.actions.settings.sub')}</div>
              </div>
              <span className="material-symbols-outlined text-brand-text-muted/50 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" style={{ fontSize: '18px' }}>
                arrow_forward
              </span>
            </button>

            <button
              onClick={() => setContactsModalOpen(true)}
              className="group relative flex items-center gap-4 p-5 bg-white border border-brand-border/70 rounded-2xl text-left overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-[0_8px_30px_-12px_rgba(45,138,95,0.25)]"
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(45, 138, 95, 0.14) 0%, rgba(45, 138, 95, 0.06) 100%)',
                }}
              >
                <span
                  className="material-symbols-outlined text-brand-success"
                  style={{ fontSize: '22px', fontVariationSettings: "'FILL' 1" }}
                >
                  groups
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-[15px] font-semibold text-brand-text leading-tight">
                  {t('dashboard.actions.contacts.title')}
                </div>
                <div className="text-xs text-brand-text-muted mt-0.5">{t('dashboard.actions.contacts.sub')}</div>
              </div>
              <span className="material-symbols-outlined text-brand-text-muted/50 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" style={{ fontSize: '18px' }}>
                arrow_forward
              </span>
            </button>

            {/* Remote lock — hidden behind FEATURES.REMOTE_LOCK_ENABLED (Wave 1.5).
                The command path does not exist end-to-end, so this card used to
                report success without locking anything. See featureFlags.ts. */}
            {FEATURES.REMOTE_LOCK_ENABLED && (
              <button
                onClick={() => setRemoteLockDrawerOpen(true)}
                className="group relative flex items-center gap-4 p-5 bg-white border border-brand-border/70 rounded-2xl text-left overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:border-red-300 hover:shadow-[0_8px_30px_-12px_rgba(220,38,38,0.25)]"
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(220, 38, 38, 0.12) 0%, rgba(220, 38, 38, 0.06) 100%)',
                  }}
                >
                  <span
                    className="material-symbols-outlined text-red-600"
                    style={{ fontSize: '22px', fontVariationSettings: "'FILL' 1" }}
                  >
                    lock
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  {/* locked-facts-allow: remote-lock — these are i18n key paths, not
                      copy. The strings themselves are parked out of the locales in
                      src/i18n/parked/remoteLock.wave1-5.json precisely so "Remote lock"
                      never reaches the bundle, and this whole block is behind
                      FEATURES.REMOTE_LOCK_ENABLED, which is false. */}
                  <div className="font-display text-[15px] font-semibold text-brand-text leading-tight">
                    {t('dashboard.actions.remoteLock.title')}
                  </div>
                  <div className="text-xs text-brand-text-muted mt-0.5">{t('dashboard.actions.remoteLock.sub')}</div>
                </div>
                <span className="material-symbols-outlined text-brand-text-muted/50 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" style={{ fontSize: '18px' }}>
                  arrow_forward
                </span>
              </button>
            )}

            {/* F05 / D13 — battery alerts. Tile carries ON/OFF only; the
                composed detail lives inside the panel. */}
            <ActionTile
              icon="battery_alert"
              tone="amber"
              title={t('dashboard.actions.battery.title')}
              titleState={batteryTileState}
              state={batteryKnown ? batteryAnyOn : undefined}
              onClick={() => setBatteryAlertsOpen(true)}
            />

            {/* F06 — medication reminders (4 generic labels, max 8 active). */}
            <button
              onClick={() => setMedicationDrawerOpen(true)}
              className="group relative flex items-center gap-4 p-5 bg-white border border-brand-border/70 rounded-2xl text-left overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-[0_8px_30px_-12px_rgba(16,185,129,0.25)]"
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(16, 185, 129, 0.14) 0%, rgba(16, 185, 129, 0.06) 100%)',
                }}
              >
                <span
                  className="material-symbols-outlined text-emerald-600"
                  style={{ fontSize: '22px', fontVariationSettings: "'FILL' 1" }}
                >
                  pill
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-[15px] font-semibold text-brand-text leading-tight">
                  {t('dashboard.actions.medication.title')}
                </div>
                <div className="text-xs text-brand-text-muted mt-0.5">{t('dashboard.actions.medication.sub')}</div>
              </div>
              <span className="material-symbols-outlined text-brand-text-muted/50 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" style={{ fontSize: '18px' }}>
                arrow_forward
              </span>
            </button>

            {/* PR #2 — Managed Mode (lock the phone into the AidFone launcher +
                set the caregiver PIN that exits it). */}
            <button
              onClick={() => setManagedModeDrawerOpen(true)}
              className="group relative flex items-center gap-4 p-5 bg-white border border-brand-border/70 rounded-2xl text-left overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-[0_8px_30px_-12px_rgba(79,70,229,0.25)]"
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(79, 70, 229, 0.14) 0%, rgba(79, 70, 229, 0.06) 100%)',
                }}
              >
                <span
                  className="material-symbols-outlined text-indigo-600"
                  style={{ fontSize: '22px', fontVariationSettings: "'FILL' 1" }}
                >
                  shield_lock
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-[15px] font-semibold text-brand-text leading-tight">
                  {t('dashboard.actions.managedMode.title')}
                </div>
                <div className="text-xs text-brand-text-muted mt-0.5">{t('dashboard.actions.managedMode.sub')}</div>
              </div>
              <span className="material-symbols-outlined text-brand-text-muted/50 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" style={{ fontSize: '18px' }}>
                arrow_forward
              </span>
            </button>
          </div>

          {/* F02 — call activity panel. 7-day grid of AM/PM + PM/Evening
              integer counts. Anomalous cells highlighted after Day 7. */}
          <CallActivityPanel seniorId={senior.id} seniorName={senior.name} />

          {/* F03 — last-known location map. Cognitive-profile warning banner
              renders above the map when senior.profile_type === 'cognitive'. */}
          <LocationPanel
            seniorId={senior.id}
            seniorName={senior.name}
            profileType={senior.profile_type as 'cognitive' | 'physical' | 'visual' | undefined}
            isOnline={senior.status === 'online'}
          />

          {/* Zone 5 — Recent Activity (timeline) */}
          <section className="mt-1">
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="font-display text-[20px] font-semibold text-brand-text tracking-[-0.01em]">
                {t('dashboard.activity.title')}
              </h3>
              <span className="text-xs text-brand-text-muted font-medium">
                {t('dashboard.lastNEvents', { count: activityEntries.length })}
              </span>
            </div>

            <div className="relative bg-white border border-brand-border/70 rounded-2xl p-5 sm:p-6">
              {/* Vertical connector line */}
              {activityEntries.length > 1 && (
                <div
                  aria-hidden
                  className="absolute left-[33px] top-10 bottom-10 w-px bg-gradient-to-b from-brand-border via-brand-border/50 to-brand-border/10"
                />
              )}

              <ul className="flex flex-col gap-4 relative">
                {activityEntries.map((entry, i) => (
                  <li key={i} className="flex items-start gap-4 relative">
                    <div
                      className={`relative w-[26px] h-[26px] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ring-4 ring-white ${entry.bg}`}
                    >
                      <span
                        className={`material-symbols-outlined ${entry.fg}`}
                        style={{ fontSize: '15px', fontVariationSettings: "'FILL' 1" }}
                      >
                        {entry.icon}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0 pb-0.5">
                      <p className="text-sm font-medium text-brand-text leading-snug">
                        {entry.label}
                      </p>
                      {entry.time && (
                        <p className="text-[11px] text-brand-text-muted mt-0.5 font-medium">
                          {entry.time}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>

              {activityEntries.length === 0 && (
                <div className="py-8 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-brand-bg-warm flex items-center justify-center">
                    <span className="material-symbols-outlined text-brand-text-muted/60" style={{ fontSize: '24px' }}>
                      history
                    </span>
                  </div>
                  <p className="text-sm text-brand-text-muted">{t('dashboard.noActivity')}</p>
                  <p className="text-xs text-brand-text-muted/70 mt-1">
                    {t('dashboard.noActivityHint', { name: senior.name })}
                  </p>
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* Modals */}
      <SettingsDrawer
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        seniorId={senior?.id || ''}
        seniorName={senior?.name || ''}
        currentLayoutId={senior?.layout_id}
        onLayoutUpdated={refetch}
      />
      <ContactsDrawer
        isOpen={contactsModalOpen}
        onClose={() => setContactsModalOpen(false)}
        seniorId={senior?.id || ''}
        seniorName={senior?.name || ''}
        onContactsUpdated={refetch}
      />
      <EditSeniorModal
        isOpen={seniorModalOpen}
        onClose={() => setSeniorModalOpen(false)}
        seniorId={senior?.id || ''}
        seniorName={senior?.name || ''}
        profileType={senior?.profile_type}
        severity={senior?.severity}
        deviceName={senior?.device_name}
        onSeniorUpdated={refetch}
        onSeniorDeleted={refetch}
      />
      <DeleteSeniorModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        seniorId={senior?.id || ''}
        seniorName={senior?.name || ''}
        onDeleted={refetch}
      />
      <ActivationCodeModal
        isOpen={codeModalOpen}
        onClose={() => setCodeModalOpen(false)}
        seniorName={senior?.name || ''}
        seniorId={senior?.id || ''}
        activationCode={senior?.activation_code || ''}
        deviceName={senior?.device_name}
        onCodeRegenerated={refetch}
      />
      <GPSZoneDrawer
        isOpen={locationModalOpen || geofenceModalOpen}
        onClose={() => {
          setLocationModalOpen(false);
          setGeofenceModalOpen(false);
        }}
        seniorId={senior?.id || ''}
        seniorName={senior?.name || ''}
        location={senior?.location}
      />
      <BatteryAlertsDrawer
        isOpen={batteryAlertsOpen}
        onClose={() => setBatteryAlertsOpen(false)}
        seniorId={senior?.id || ''}
        seniorName={senior?.name || t('dashboard.seniorFallback')}
        profileType={senior?.profile_type}
        caregiverPhone={caregiver?.phone}
        caregiverSmsOptedOut={caregiver?.sms_opted_out}
        onFlagsUpdated={refetch}
      />
      {/* D20 — Billing, reachable at last. The portal is where billing history,
          card changes AND cancellation live, so this is the easy-cancellation
          path we promise. */}
      <BillingDrawer
        isOpen={billingOpen}
        onClose={() => setBillingOpen(false)}
        seniorId={senior?.id || ''}
        trialEndsAt={senior?.trial_ends_at}
        subscriptionStatus={senior?.subscription_status}
      />
      <CurrentModelDrawer
        isOpen={currentModelOpen}
        onClose={() => setCurrentModelOpen(false)}
        layout={layoutConfig ?? null}
        layoutId={senior?.layout_id}
      />
      <ScamBlockingDrawer
        isOpen={scamBlockingOpen}
        onClose={() => setScamBlockingOpen(false)}
        seniorId={senior?.id || ''}
        seniorName={senior?.name || t('dashboard.seniorFallback')}
        onUpdated={refetch}
      />
      <VoicemailDrawer
        isOpen={voicemailDrawerOpen}
        onClose={() => setVoicemailDrawerOpen(false)}
        seniorId={senior?.id || ''}
        seniorName={senior?.name || t('dashboard.seniorFallback')}
        caregiverPhone={caregiver?.phone}
        caregiverSmsOptedOut={caregiver?.sms_opted_out}
      />
      {/* Only render once both senior + caregiver are loaded — empty-string
          ids would otherwise produce opaque uuid-format errors on the
          first INSERT (the drawer's empty-id guard surfaces them now, but
          this avoids the noise entirely). Guard on `caregiver.id` (not just
          truthy `caregiver`) because the Caregiver type marks `id?` optional
          while typescript-narrows-the-object-but-not-its-fields. */}
      {senior && caregiver?.id && (
        <MedicationRemindersDrawer
          isOpen={medicationDrawerOpen}
          onClose={() => setMedicationDrawerOpen(false)}
          seniorId={senior.id}
          seniorName={senior.name || t('dashboard.seniorFallback')}
          caregiverId={caregiver.id}
        />
      )}
      <ManagedModeDrawer
        isOpen={managedModeDrawerOpen}
        onClose={() => { setManagedModeDrawerOpen(false); setPinBannerKey((k) => k + 1); }}
        seniorId={senior?.id || ''}
        seniorName={senior?.name || ''}
      />
      {/* Wiring preserved for the Wave 1.5 rebuild, gated OFF so it cannot mount.
          The callbacks below are still stubs — they must hit the real edge
          function BEFORE FEATURES.REMOTE_LOCK_ENABLED is flipped on. */}
      {FEATURES.REMOTE_LOCK_ENABLED && (
        <RemoteLockDrawer
          isOpen={remoteLockDrawerOpen}
          onClose={() => setRemoteLockDrawerOpen(false)}
          seniorId={senior?.id || ''}
          seniorName={senior?.name || ''}
          isLocked={false /* TODO: wire once activity_events table exists (Task 28, deferred) */}
          onLock={async () => {
            // TODO: call remote-lock edge function once deployed (Task 26, deferred)
            console.log('[RemoteLock] Lock requested for senior:', senior?.id);
          }}
          onUnlock={async () => {
            // TODO: call remote-lock edge function with REMOTE_UNLOCK (Task 26, deferred)
            console.log('[RemoteLock] Unlock requested for senior:', senior?.id);
          }}
        />
      )}

    </DashboardLayout>
  );
};

export default Dashboard;
